import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import BackLink from '../BackLink';
import { motion, useScroll, useSpring, useInView, useReducedMotion, animate } from 'framer-motion';
import Footer from '../Footer';
import { SERIES } from '../blogPosts';
import './PandaReflexes.css';

const POST_URL = 'https://www.steepwind.com/blog/panda-gets-reflexes';
const PULL_QUOTE = 'Most of the work went into how we describe the screen, not into the model.';

// Labelled screen steps, bucketed by the gap between Jev's first and second choice.
const GAP_BUCKETS = [
  { label: '< 0.10', min: 0, n: 6, right: 1 },
  { label: '0.10–0.25', min: 0.1, n: 7, right: 6 },
  { label: '0.25–0.50', min: 0.25, n: 20, right: 20 },
  { label: '≥ 0.50', min: 0.5, n: 65, right: 63 },
];
const TOTAL_STEPS = GAP_BUCKETS.reduce((a, b) => a + b.n, 0);

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.6, ease: 'easeOut' },
};

// Reduced-motion users get content in place, with no fade-in.
function useReveal() {
  return useReducedMotion() ? {} : reveal;
}

function ReadingProgress() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, restDelta: 0.001 });
  return <motion.div className="rx-progress" style={{ scaleX }} aria-hidden="true" />;
}

function CountUp({ to, decimals = 0, suffix = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const reduce = useReducedMotion();
  const [value, setValue] = useState(reduce ? to : 0);
  useEffect(() => {
    if (!inView || reduce) return undefined;
    const controls = animate(0, to, { duration: 1.4, ease: 'easeOut', onUpdate: setValue });
    return () => controls.stop();
  }, [inView, reduce, to]);
  return (
    <span ref={ref} aria-label={`${to.toFixed(decimals)}${suffix}`}>
      <span aria-hidden="true">{value.toFixed(decimals)}{suffix}</span>
    </span>
  );
}

function Stat({ children, label }) {
  const rv = useReveal();
  return (
    <motion.div className="rx-stat" {...rv}>
      <b>{children}</b>
      <span>{label}</span>
    </motion.div>
  );
}

function ShareBar() {
  const [copied, setCopied] = useState(false);
  const text = encodeURIComponent('Panda gets reflexes: how Steepwind put a fast classifier in front of the LLM in a phone agent');
  const url = encodeURIComponent(POST_URL);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(POST_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      window.prompt('Copy this link', POST_URL);
    }
  };
  return (
    <div className="rx-share" role="group" aria-label="Share this post">
      <a href={`https://x.com/intent/post?text=${text}&url=${url}`} target="_blank" rel="noopener noreferrer">Post on X</a>
      <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${url}`} target="_blank" rel="noopener noreferrer">LinkedIn</a>
      <button type="button" onClick={copy} aria-live="polite">{copied ? 'Link copied' : 'Copy link'}</button>
    </div>
  );
}

function Arrowheads({ prefix }) {
  return (
    <defs>
      {[['a', 'rx-ah'], ['r', 'rx-ah-ref'], ['t', 'rx-ah-th']].map(([k, cls]) => (
        <marker key={k} id={`${prefix}${k}`} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M0,0L10,5L0,10z" className={cls} />
        </marker>
      ))}
    </defs>
  );
}

function LoopDiagram() {
  return (
    <svg viewBox="0 0 880 330" role="img" aria-labelledby="rx-d1t rx-d1d">
      <title id="rx-d1t">The Panda step loop with Jev in front of the LLM</title>
      <desc id="rx-d1d">Screen and goal go to Jev, which picks from a closed list of moves. Our code checks confidence. Confident picks are done right away. Unsure picks, typed text and finishing the task go to the LLM. The new screen loops back.</desc>
      <Arrowheads prefix="l" />
      <path id="rx-fast" d="M170,97 H690" fill="none" stroke="none" />
      <path id="rx-slow" d="M170,97 H535 V243 H775 V134" fill="none" stroke="none" />

      <rect x="20" y="60" width="150" height="74" rx="8" className="rx-box" />
      <text x="95" y="92" textAnchor="middle" className="rx-t-ink rx-strong">Screen</text>
      <text x="95" y="114" textAnchor="middle" className="rx-t-mut rx-small">goal + a11y tree</text>

      <rect x="230" y="60" width="170" height="74" rx="8" className="rx-box-ref" />
      <text x="315" y="92" textAnchor="middle" className="rx-t-ref rx-strong">Jev picks</text>
      <text x="315" y="114" textAnchor="middle" className="rx-t-mut rx-small">from a closed list</text>

      <rect x="460" y="60" width="150" height="74" rx="8" className="rx-box" />
      <text x="535" y="92" textAnchor="middle" className="rx-t-ink rx-strong">Check</text>
      <text x="535" y="114" textAnchor="middle" className="rx-t-mut rx-small">rules, not a model</text>

      <rect x="690" y="60" width="170" height="74" rx="8" className="rx-box" />
      <text x="775" y="92" textAnchor="middle" className="rx-t-ink rx-strong">Act</text>
      <text x="775" y="114" textAnchor="middle" className="rx-t-mut rx-small">tap · scroll · open</text>

      <rect x="460" y="206" width="150" height="74" rx="8" className="rx-box-th" />
      <text x="535" y="238" textAnchor="middle" className="rx-t-th rx-strong">LLM</text>
      <text x="535" y="260" textAnchor="middle" className="rx-t-mut rx-small">thinks it through</text>

      <line x1="170" y1="97" x2="226" y2="97" className="rx-ln" markerEnd="url(#la)" />
      <line x1="400" y1="97" x2="456" y2="97" className="rx-ln" markerEnd="url(#la)" />
      <line x1="610" y1="97" x2="686" y2="97" className="rx-ln-ref" markerEnd="url(#lr)" />
      <text x="648" y="86" textAnchor="middle" className="rx-t-ref rx-small">sure</text>
      <line x1="535" y1="134" x2="535" y2="202" className="rx-ln-th" markerEnd="url(#lt)" />
      <text x="545" y="166" className="rx-t-th rx-small">unsure, text,</text>
      <text x="545" y="182" className="rx-t-th rx-small">or &quot;done?&quot;</text>
      <path d="M610,243 H775 V138" className="rx-ln-th" markerEnd="url(#lt)" />
      <path d="M775,60 V24 H95 V56" className="rx-ln" markerEnd="url(#la)" />
      <text x="435" y="17" textAnchor="middle" className="rx-t-mut rx-small">next screen</text>

      {/* Moving dots: most steps take the fast path, a few take the slow one. */}
      <g className="rx-pulses" aria-hidden="true">
        {[0, 0.6, 1.2].map((begin) => (
          <circle key={begin} r="6" className="rx-dot-ref">
            <animateMotion dur="1.8s" begin={`${begin}s`} repeatCount="indefinite">
              <mpath href="#rx-fast" />
            </animateMotion>
          </circle>
        ))}
        <circle r="6" className="rx-dot-th">
          <animateMotion dur="4.2s" begin="0.3s" repeatCount="indefinite">
            <mpath href="#rx-slow" />
          </animateMotion>
        </circle>
      </g>
    </svg>
  );
}

function GapChart() {
  const reduce = useReducedMotion();
  const x0 = 70;
  const slot = 137.5;
  const y = (pct) => 240 - pct * 2.1;
  return (
    <svg viewBox="0 0 640 300" role="img" aria-labelledby="rx-c1t rx-c1d">
      <title id="rx-c1t">Accuracy by gap between Jev&apos;s top two choices</title>
      <desc id="rx-c1d">Gap under 0.1: 17 percent correct, 6 steps. Gap 0.1 to 0.25: 86 percent, 7 steps. Gap 0.25 to 0.5: 100 percent, 20 steps. Gap 0.5 or more: 97 percent, 65 steps.</desc>
      {[0, 25, 50, 75, 100].map((p) => (
        <line key={p} x1="70" x2="620" y1={y(p)} y2={y(p)} className="rx-grid" />
      ))}
      {[0, 50, 100].map((p) => (
        <text key={p} x="60" y={y(p) + 4} textAnchor="end" className="rx-t-mut rx-small">{p}%</text>
      ))}
      {GAP_BUCKETS.map((b, i) => {
        const pct = Math.round((b.right / b.n) * 100);
        const cx = x0 + slot * i + slot / 2;
        const cls = b.min >= 0.25 ? 'rx-bar-ref' : b.min === 0 ? 'rx-bar-bad' : 'rx-bar-mut';
        const tcls = b.min >= 0.25 ? 'rx-t-ref' : b.min === 0 ? 'rx-t-bad' : 'rx-t-ink';
        return (
          <g key={b.label}>
            <motion.rect
              x={cx - 45}
              width="90"
              y={y(pct)}
              height={240 - y(pct)}
              rx="3"
              className={cls}
              style={{ transformBox: 'fill-box', transformOrigin: 'bottom' }}
              initial={reduce ? false : { scaleY: 0 }}
              whileInView={{ scaleY: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.15 * i, ease: 'easeOut' }}
            />
            <text x={cx} y={y(pct) - 8} textAnchor="middle" className={`${tcls} rx-strong`}>{pct}%</text>
            <text x={cx} y="260" textAnchor="middle" className="rx-t-ink rx-small">{b.label}</text>
            <text x={cx} y="277" textAnchor="middle" className="rx-t-mut rx-tiny">n = {b.n}</text>
          </g>
        );
      })}
      <text x="345" y="296" textAnchor="middle" className="rx-t-mut rx-tiny">gap between Jev&apos;s first and second choice</text>
    </svg>
  );
}

function ThresholdPlayground() {
  const [min, setMin] = useState(0.25);
  const kept = GAP_BUCKETS.filter((b) => b.min >= min);
  const n = kept.reduce((a, b) => a + b.n, 0);
  const right = kept.reduce((a, b) => a + b.right, 0);
  const share = Math.round((n / TOTAL_STEPS) * 100);
  const acc = n ? ((right / n) * 100).toFixed(1) : '0';
  const wrong = n - right;
  return (
    <div className="rx-play">
      <div className="rx-play-head">
        <span className="rx-eyebrow">Try it</span>
        <p>Pick how big Jev&apos;s lead must be before Panda acts without the LLM.</p>
      </div>
      <div className="rx-seg" role="radiogroup" aria-label="Minimum gap before acting">
        {GAP_BUCKETS.map((b) => (
          <button
            key={b.min}
            type="button"
            role="radio"
            aria-checked={min === b.min}
            className={min === b.min ? 'is-on' : ''}
            onClick={() => setMin(b.min)}
          >
            {b.min === 0 ? 'Always act' : `gap ≥ ${b.min.toFixed(2)}`}
          </button>
        ))}
      </div>
      <div className="rx-play-out" aria-live="polite">
        <div>
          <b>{share}%</b>
          <span>of steps skip the LLM</span>
          <div className="rx-meter"><motion.i animate={{ width: `${share}%` }} transition={{ type: 'spring', stiffness: 140, damping: 20 }} /></div>
        </div>
        <div>
          <b>{acc}%</b>
          <span>of those picks are right</span>
          <div className="rx-meter"><motion.i animate={{ width: `${acc}%` }} transition={{ type: 'spring', stiffness: 140, damping: 20 }} /></div>
        </div>
        <div>
          <b className={wrong > 2 ? 'rx-bad' : ''}>{wrong}</b>
          <span>wrong taps out of {TOTAL_STEPS} steps</span>
        </div>
      </div>
      <p className="rx-play-note">Panda ships with a 0.25 gap. The live check also looks at the top probability, so in the replay it acted on 85% of steps.</p>
    </div>
  );
}

function DoneChart() {
  const reduce = useReducedMotion();
  const sx = (v) => 70 + v * 540;
  const grow = (delay) => ({
    style: { transformBox: 'fill-box', transformOrigin: 'left' },
    initial: reduce ? false : { scaleX: 0 },
    whileInView: { scaleX: 1 },
    viewport: { once: true },
    transition: { duration: 0.9, delay, ease: 'easeOut' },
  });
  return (
    <svg viewBox="0 0 640 170" role="img" aria-labelledby="rx-c2t rx-c2d">
      <title id="rx-c2t">Overlap of Jev&apos;s goal-done probability on finished and unfinished steps</title>
      <desc id="rx-c2d">On the 32 steps where the task really was finished, Jev&apos;s done probability went as low as 0.10, with a mean of 0.65. On unfinished steps it reached 0.94. The two ranges overlap from 0.10 to 0.94.</desc>
      <line x1="70" y1="130" x2="610" y2="130" className="rx-grid" />
      {[0, 0.5, 1].map((v) => (
        <g key={v}>
          <line x1={sx(v)} x2={sx(v)} y1="126" y2="134" className="rx-ln" />
          <text x={sx(v)} y="150" textAnchor="middle" className="rx-t-mut rx-small">{v === 1 ? '1.0' : v}</text>
        </g>
      ))}
      <text x="70" y="26" className="rx-t-ref rx-small rx-strong">Task really finished (32 steps)</text>
      <motion.rect x={sx(0.1)} y="36" width={sx(1) - sx(0.1)} height="20" rx="3" className="rx-bar-ref" {...grow(0)} />
      <circle cx={sx(0.65)} cy="46" r="6" className="rx-dot-ink" />
      <text x={sx(0.65)} y="72" textAnchor="middle" className="rx-t-ink rx-tiny">mean 0.65</text>
      <text x={sx(0.1)} y="72" textAnchor="middle" className="rx-t-mut rx-tiny">min 0.10</text>
      <text x="70" y="96" className="rx-t-bad rx-small rx-strong">Not finished yet</text>
      <motion.rect x={sx(0)} y="104" width={sx(0.94) - sx(0)} height="14" rx="3" className="rx-bar-bad" {...grow(0.3)} />
      <text x={sx(0.94)} y="98" textAnchor="middle" className="rx-t-bad rx-tiny">max 0.94</text>
    </svg>
  );
}

const ROUTES = [
  { y: 37, name: 'Run the tool', note: 'no LLM', time: '0.44 s', cls: 'rx-box-ref', fast: true },
  { y: 95, name: 'Fill inputs', note: 'one small call', time: '~3.3 s', cls: 'rx-box' },
  { y: 153, name: 'Hand off to agent', note: '', time: '2.6–3.5 s', cls: 'rx-box' },
  { y: 211, name: 'Chat reply', note: '', time: '1.7 s', cls: 'rx-box' },
  { y: 269, name: 'Unsure', note: 'old LLM turn, unchanged', time: '', cls: 'rx-box-th', slow: true },
];

function RouterDiagram() {
  const reduce = useReducedMotion();
  return (
    <svg viewBox="0 0 880 300" role="img" aria-labelledby="rx-d2t rx-d2d">
      <title id="rx-d2t">Voice routing with Jev</title>
      <desc id="rx-d2d">What you say goes to Jev, which sends it one of five ways: run a tool directly with no LLM in 0.44 seconds; one small LLM call to fill a tool&apos;s inputs in about 3.3 seconds; hand off to the phone agent in 2.6 to 3.5 seconds; a chat reply in 1.7 seconds; or, if Jev is unsure, the old single LLM turn.</desc>
      <Arrowheads prefix="v" />
      <rect x="20" y="113" width="150" height="70" rx="8" className="rx-box" />
      <text x="95" y="143" textAnchor="middle" className="rx-t-ink rx-strong">You speak</text>
      <text x="95" y="164" textAnchor="middle" className="rx-t-mut rx-small">&quot;what&apos;s my battery&quot;</text>
      <rect x="230" y="113" width="150" height="70" rx="8" className="rx-box-ref" />
      <text x="305" y="143" textAnchor="middle" className="rx-t-ref rx-strong">Jev routes</text>
      <text x="305" y="164" textAnchor="middle" className="rx-t-mut rx-small">one choice call</text>
      <line x1="170" y1="148" x2="226" y2="148" className="rx-ln" markerEnd="url(#va)" />
      {ROUTES.map((r, i) => (
        <g key={r.name}>
          <motion.path
            d={`M380,148 C440,148 440,${r.y} 496,${r.y}`}
            className={r.fast ? 'rx-ln-ref' : r.slow ? 'rx-ln-th' : 'rx-ln'}
            markerEnd={`url(#v${r.fast ? 'r' : r.slow ? 't' : 'a'})`}
            initial={reduce || r.slow ? false : { pathLength: 0 }}
            whileInView={{ pathLength: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.12 * i }}
          />
          <rect x="500" y={r.y - 23} width="360" height="46" rx="8" className={r.cls} />
          <text x="516" y={r.y + 5} className={r.slow ? 'rx-t-th rx-small' : 'rx-t-ink rx-small'}>
            <tspan className="rx-strong">{r.name}</tspan>{r.note ? ` · ${r.note}` : ''}
          </text>
          {r.time && (
            <text x="844" y={r.y + 5} textAnchor="end" className={`${r.fast ? 'rx-t-ref' : 'rx-t-ink'} rx-small rx-strong`}>{r.time}</text>
          )}
        </g>
      ))}
    </svg>
  );
}

function Figure({ wide, caption, children }) {
  const rv = useReveal();
  return (
    <div className={wide ? 'rx-wide' : undefined}>
      <motion.figure className="rx-fig" {...rv}>
        <div className="rx-fig-box">{children}</div>
        <figcaption>{caption}</figcaption>
      </motion.figure>
    </div>
  );
}

function PullQuote({ text }) {
  const rv = useReveal();
  const share = `https://x.com/intent/post?text=${encodeURIComponent(`"${text}"`)}&url=${encodeURIComponent(POST_URL)}`;
  return (
    <motion.blockquote className="rx-pull" {...rv}>
      <p>{text}</p>
      <a href={share} target="_blank" rel="noopener noreferrer">Post this quote on X</a>
    </motion.blockquote>
  );
}

const TITLE_WORDS = ['Panda', 'gets', 'reflexes'];

export default function PandaReflexes() {
  const reduce = useReducedMotion();
  const series = SERIES['under-the-hood'];
  const rv = useReveal();

  useEffect(() => {
    document.title = 'Panda Gets Reflexes | Steepwind Labs';
  }, []);

  return (
    <div className="rx-page">
      <ReadingProgress />
      <article className="rx-article">
        <header className="rx-header">
          <BackLink to="/blog" className="rx-back">All posts</BackLink>
          <div className="rx-kicker">
            <span className="rx-series-chip">{series.name} · Part 1</span>
            <span>Engineering</span>
          </div>
          <h1>
            {TITLE_WORDS.map((w, i) => (
              <motion.span
                key={w}
                className={i === 2 ? 'rx-hl' : ''}
                initial={reduce ? false : { opacity: 0, y: 28, filter: 'blur(6px)' }}
                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                transition={{ duration: 0.6, delay: 0.12 * i, ease: 'easeOut' }}
              >
                {w}{i < TITLE_WORDS.length - 1 ? ' ' : ''}
              </motion.span>
            ))}
          </h1>
          <motion.p
            className="rx-dek"
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.45 }}
          >
            We put a fast classifier in front of the LLM in our phone agent. Most screen decisions now take about 0.4
            seconds, and Panda knows when to hand the decision to something smarter.
          </motion.p>
          <div className="rx-byline">
            <img src="/panda.png" alt="" width="40" height="40" />
            <div>
              <strong>Steepwind Labs engineering</strong>
              <span>October 2026 · 8 min read</span>
            </div>
          </div>
          <ShareBar />
        </header>

        <div className="rx-body">
          <p className="rx-lead">
            Panda is a voice assistant that operates an Android phone for people who find touch screens hard to use.
            You say &quot;open Instagram&quot; or &quot;play We Will Rock You on YouTube&quot;, and Panda reads the screen
            through the accessibility tree and taps, scrolls and types for you.
          </p>
          <p>
            Until this release, every one of those steps went through a frontier LLM. The model read the goal and the
            screen, thought about it, and returned one action. That works, but most steps are boring. &quot;Tap the
            search bar&quot; does not need a large model to reason in prose for two seconds. For a user who is waiting
            with the phone in their hand, those seconds add up.
          </p>
          <p>
            So we split the agent into two parts, the way people work: a fast reflex for the obvious moves, and slower
            thinking for the moves that need it.
          </p>

          <div className="rx-stats rx-wide">
            <Stat label="median Jev decision per screen step (p90 0.55 s)"><CountUp to={0.38} decimals={2} suffix=" s" /></Stat>
            <Stat label="of confident step picks were correct"><CountUp to={97.6} decimals={1} suffix="%" /></Stat>
            <Stat label="of steps were confident enough to skip the LLM"><CountUp to={85} suffix="%" /></Stat>
            <Stat label="confident voice routes correct, Hinglish and typos included"><CountUp to={80} />/80</Stat>
          </div>

          <h2>What Jev is</h2>
          <p>
            <a href="https://typesafe.ai/blog/introducing-system-one-models-and-jev" target="_blank" rel="noopener noreferrer">Jev</a>{' '}
            is a &quot;System One&quot; model from TypeSafe. It does not generate text. You give it a state and a fixed
            set of typed questions, such as &quot;which of these options?&quot; or &quot;is this true?&quot;, and it
            answers all of them in one pass with a probability for each option.
          </p>
          <p>
            That shape suits a phone agent. Before we call Jev we already know every legal move on the screen: the
            elements that can be tapped, the scroll directions, back, home, the installed apps. Jev only has to pick
            one. It cannot invent an element that is not there, and the probabilities tell us how sure it is.
          </p>

          <h2>Where it sits in the loop</h2>
          <p>
            Each step, Panda reads the screen, turns it into a short list of candidate moves, and asks Jev to choose.
            Our own code then looks at the answer and decides whether to trust it. If Jev is sure, Panda acts right
            away. If it is not, the step goes to the LLM exactly as it did before.
          </p>
          <Figure wide caption="One step of the agent. The cream path skips the LLM. The amber path is the old behaviour, kept as the fallback.">
            <LoopDiagram />
          </Figure>

          <p>Three rules came out of testing. They live in code, not in model tuning:</p>
          <ol className="rx-rules">
            {[
              ['Jev never writes text.', 'It may decide the next move is "type into the search box", but the words always come from the LLM or straight from what the user said.'],
              ['Jev never ends a task on its own.', 'Its "is the goal done?" signal is a hint. A separate check makes the final call.'],
              ['Sure means a clear winner.', "We don't trust the top probability alone. We look at the gap between Jev's first and second choice."],
            ].map(([t, d], i) => (
              <motion.li key={t} {...rv} transition={{ ...reveal.transition, delay: 0.1 * i }}>
                <b>{t}</b> {d}
              </motion.li>
            ))}
          </ol>

          <h2>How we tested it</h2>
          <p>
            Every agent run on our test phone writes a trace: the screen at each step, what the agent saw, what Jev
            answered, and what happened next. We pulled 49 of these runs, successes and failures, off a mid-range
            Android phone and replayed every step against Jev offline. A step counts as correct if Jev chose the move a
            person would. For failed runs, we labelled the right move by hand.
          </p>
          <p>
            That gave us 130 steps, 98 of them labelled, across YouTube, Instagram, WhatsApp, X, Maps and the
            launcher. The replay needs no phone, so we could change how we describe the screen and rerun the whole set
            in under a minute.
          </p>

          <h2>Confidence is the real product</h2>
          <p>
            Raw accuracy over all labelled steps was 91.8%. That alone is not good enough to let a model tap buttons on
            someone&apos;s phone. The useful number is accuracy split by how decisive Jev was.
          </p>
          <Figure caption="98 labelled screen steps, replayed offline. Panda acts on Jev's pick in the cream buckets. The rest go to the LLM.">
            <GapChart />
          </Figure>
          <p>
            When the top two options were close, Jev was right one time in six. Once the winner led by a clear margin,
            it was right 97–100% of the time. The confidence signal separates good picks from bad ones, and that is
            what lets us give a classifier control of a real phone.
          </p>

          <ThresholdPlayground />

          <PullQuote text={PULL_QUOTE} />

          <p>
            We never trained or fine-tuned Jev. Every improvement came from changing what it sees: how we list
            elements, how we summarise the last few moves, and how options are worded. One revision of the screen
            description, on the same recorded runs, moved these numbers:
          </p>
          <motion.div className="rx-table" {...rv}>
            <table>
              <thead>
                <tr><th scope="col">Metric</th><th scope="col">Before</th><th scope="col">After</th></tr>
              </thead>
              <tbody>
                <tr><td>Accuracy, all labelled steps</td><td>87.8%</td><td className="rx-up">91.8%</td></tr>
                <tr><td>Steps confident enough to act</td><td>78.4%</td><td className="rx-up">84.7%</td></tr>
                <tr><td>Accuracy when confident</td><td>98.3%</td><td>97.6%</td></tr>
                <tr><td>Median latency</td><td>403 ms</td><td className="rx-up">379 ms</td></tr>
                <tr><td>p90 latency</td><td>1,726 ms</td><td className="rx-up">547 ms</td></tr>
              </tbody>
            </table>
          </motion.div>
          <p className="rx-note">The labelled set grew between the two runs (74 to 98 steps), so read this as a direction, not a controlled A/B test.</p>

          <h2>Why Jev doesn&apos;t get to say &quot;done&quot;</h2>
          <p>Jev answers &quot;is the goal done?&quot; on every step. We hoped to use it to stop runs early. The data said no.</p>
          <Figure caption={'Jev\'s "goal done" probability. No cut-off separates the two groups, so this signal can\'t end a run by itself.'}>
            <DoneChart />
          </Figure>
          <p>
            Finished and unfinished steps overlap almost completely. Any cut-off either stops tasks too early or never
            stops them. So &quot;done&quot; stays with a separate check, and Jev&apos;s score is one input to it.
          </p>
          <p>
            Typed text has a similar story. In an early prototype we ran Jev alone, with no LLM, on &quot;open youtube
            and play we will rock you&quot;. It opened YouTube, closed a system popup, searched, tapped a result and
            finished in 6 steps. The LLM-only agent, on the same task, took 21 logged events, tried two specialised
            search tools that both failed, and drifted into a system dialog without finishing.
          </p>
          <p>
            But Jev typed <code>youtube and play we will rock you</code> into the search box instead of{' '}
            <code>we will rock you</code>. YouTube&apos;s search forgave it that time. A text field in a banking or
            messaging app would not. That run is why Jev never chooses the words.
          </p>

          <h2>Picking the right app</h2>
          <p>
            &quot;Open X&quot; sounds trivial until a phone has 165 launchable apps and the user says &quot;tweet about
            monkeys&quot; or &quot;dm usman&quot;. We give Jev the installed app list as options and ask it to pick.
          </p>
          <motion.div className="rx-apps" {...rv}>
            <div className="rx-apps-score"><b><CountUp to={26} />/26</b><span>right when decisive · median ~0.4 s · 165 apps</span></div>
            <ul>
              <li><span>&quot;tweet about monkeys&quot;</span><em>X</em></li>
              <li><span>&quot;open insta&quot;</span><em>Instagram</em></li>
              <li><span>&quot;dm usman&quot;</span><em>WhatsApp</em></li>
              <li><span>&quot;order groceries&quot;</span><em>Blinkit</em></li>
              <li className="rx-held"><span>&quot;watch reels&quot;</span><em>held back: Instagram or YouTube?</em></li>
              <li className="rx-held"><span>&quot;listen to bohemian rhapsody&quot;</span><em>held back: Spotify or YouTube?</em></li>
            </ul>
          </motion.div>
          <p>
            Across 28 test prompts Jev picked the right app, or a fair alternative, every time. The two it held back on
            are the ones a person would ask about too. On the phone, &quot;open insta&quot; from the home screen puts
            Instagram on screen about 7 seconds after the user taps Run, and Jev accounts for about 2 seconds of that.
            The rest is the phone itself: the launcher animation, and waiting for the screen to settle.
          </p>

          <h2>Reflexes for voice, too</h2>
          <p>
            The same idea works one level up. When someone speaks to Panda, the first question is what kind of request
            it is: a chat question, a direct phone action like &quot;what&apos;s my battery&quot;, or a multi-step task
            for the agent. That used to be one large LLM call per request. Now Jev routes it first.
          </p>
          <Figure wide caption="Measured on device with Jev warm, from end of speech to Panda acting or starting to speak.">
            <RouterDiagram />
          </Figure>
          <p>
            We wrote 87 test requests. Some are clean (&quot;read my notifications&quot;). Others are what speech
            recognition really produces: typos, Hinglish, requests that need no tool, and multi-step asks. Jev routed
            85 of 87 correctly, and on the 80 where it was decisive it was right 80 times. The two misses were close
            calls that went to the LLM anyway: &quot;reply to the last whatsapp message saying ok&quot; and &quot;cancel
            my uber&quot;.
          </p>
          <p>
            For users, this means a request like &quot;what&apos;s my battery&quot; goes from end of speech to an answer in
            under half a second, with no LLM call at all. When Jev is unsure, the request goes to the same
            conversational turn Panda already had. Jev can make a request faster. It can&apos;t make chat worse.
          </p>

          <h2>What we learned</h2>
          <div className="rx-lessons">
            {[
              ['Use a classifier where the options are known.', 'On a phone screen, they are. Asking a generative model to write "tap element 7" as text and parsing it wastes its strengths and adds ways to fail.'],
              ['Calibration beats accuracy.', 'A model that is 92% right overall but 98% right when it says it is sure is far more useful than one that is 95% right with no idea which 5% are wrong.'],
              ['Record everything from day one.', 'The replay set came from ordinary test runs. It turned each change into a sub-minute experiment, and it is where every number in this post comes from.'],
              ['Latency lives in boring places.', 'Once decisions took 0.4 s, the slow parts were cold connections, a cloud model warming up after a few idle minutes (1.3–1.5 s), and waiting for screen animations. Those are next.'],
            ].map(([t, d], i) => (
              <motion.div key={t} className="rx-lesson" {...rv} transition={{ ...reveal.transition, delay: 0.08 * i }}>
                <b>{t}</b>
                <p>{d}</p>
              </motion.div>
            ))}
          </div>

          <h2>What&apos;s next</h2>
          <p>
            The reflex layer is on in the current Panda app on Google Play. Next we are cutting the wait for the screen
            to settle, improving how the LLM writes message text when Jev hands it a typing step, and growing the
            replay set past a few hundred steps so the cut-offs rest on more data.
          </p>
          <p>
            If you build agents that act on real interfaces, try the split. Let a fast model choose from a closed set,
            let code decide when to trust it, and keep the large model for the moves that need thought.
          </p>

          <motion.aside className="rx-series" {...rv} aria-label={`${series.name} series`}>
            <span className="rx-eyebrow">{series.name} series</span>
            <p>{series.blurb}</p>
            <ol>
              <li className="is-current"><span>Part 1</span> Panda gets reflexes <em>You are here</em></li>
              <li><span>Part 2</span> Coming soon</li>
            </ol>
            <div className="rx-series-links">
              <Link to="/changelog">See every feature we ship in the changelog →</Link>
            </div>
          </motion.aside>

          <ShareBar />

          <p className="rx-foot">
            Numbers are from internal testing in September 2026: 49 recorded agent runs (130 steps, 98 labelled) on one
            Android test phone, 28 app-pick prompts against 165 installed apps, and 87 voice-routing requests. Jev is a
            product of TypeSafe AI. This post describes our own integration of it.
          </p>
        </div>
      </article>
      <Footer />
    </div>
  );
}
